<template>
  <v-card v-if="quote.instructed != 0">
    <v-card-text>
      <h2><v-icon>email</v-icon>Emails</h2>
      <v-btn :to="{ name: 'quoteEmail', params: { id: quote.id } }" small>
        <v-icon>add</v-icon>Email address
      </v-btn>
      <table border="1" v-if="quote.quoteEmails">
        <tr>
          <th>Address</th>
          <th>Personal</th>
          <th>Validated</th>
          <th />
        </tr>
        <tr
          v-for="(quoteEmail, qeindex) in quote.quoteEmails"
          :key="qeindex"
          :class="quoteEmail.archived ? 'emailArchived' : ''"
        >
          <template v-if="quoteEmail.email">
            <td>
              <a :href="`mailto:${quoteEmail.email.address}`">{{
                quoteEmail.email.address
              }}</a>
            </td>
            <td>{{ quoteEmail.email.personal }}</td>
            <td>{{ quoteEmail.email.validated }}</td>
            <td>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    :to="{
                      name: 'quoteEmail',
                      params: {
                        id: $route.params.id,
                        emailId: quoteEmail.email.id
                      }
                    }"
                    small
                    v-on="on"
                  >
                    <v-icon>edit</v-icon>Edit
                  </v-btn>
                </template>
                <span>Edit the personal detail of this address</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    @click="removeEmail(quoteEmail)"
                    color="error"
                    small
                    v-on="on"
                    v-if="!quoteEmail.archived"
                  >
                    <v-icon>delete</v-icon>Delete
                  </v-btn>
                </template>
                <span>Remove this email address from the quote</span>
              </v-tooltip>
            </td>
          </template>
          <td colspan="4" v-if="!quoteEmail.email">
            <v-progress-circular indeterminate color="info" />
          </td>
        </tr>
      </table>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-switch
            v-model="excludeOffers"
            :label="
              excludeOffers
                ? 'Will not receive email offers'
                : 'Will receive email offers'
            "
            v-on="on"
            @change="updateExcludeOffers"
          />
        </template>
        <span>Excluding from offers will send unsubscription notification</span>
      </v-tooltip>
      <v-switch
        v-model="callback"
        :label="
          callback
            ? 'Client is waiting for a callback'
            : 'Client is not waiting for a callback'
        "
        @change="updateCallback"
      />
      <p>
        <v-btn
          :to="{ name: 'scheduleEmails', params: { id: $route.params.id } }"
          color="info"
        >
          <v-icon>send</v-icon>Send quote by email
        </v-btn>
      </p>
      <div v-if="$keycloak.hasRealmRole('root')">
        <span>&#160;</span>
        <a @click="showQuoteFormatted('html')">html</a>
        <span>&#160;</span>
        <a @click="showQuoteFormatted('text')">text</a>
        <span>&#160;</span>
        <a @click="showQuoteFormatted('fop')">fop</a>
      </div>
      <div style="overflow: auto; overflow-y: hidden" v-if="quote.logs">
        <v-divider />
        <h3>Sent emails</h3>
        <div>These emails have been sent on this quote</div>
        <table border="1">
          <tr>
            <th>Subject</th>
            <th>Emails</th>
            <th>User</th>
            <th>Time</th>
          </tr>
          <tr v-for="log in quote.logs" :key="log.id">
            <td>
              <div v-if="log.emailTemplateRevision">
                <router-link
                  :to="{
                    name: 'emailTemplateRevisionSales',
                    params: {
                      id: log.emailTemplateRevision.emailTemplate.id,
                      revisionid: log.emailTemplateRevision.id
                    }
                  }"
                  :is="
                    $keycloak.hasRealmRole('salesadmin')
                      ? 'router-link'
                      : 'span'
                  "
                >
                  {{ log.emailTemplateRevision.subject }}</router-link
                >
              </div>
              <div v-if="!log.emailTemplateRevision">
                <span v-if="log.logAction">{{ log.logAction.body }}</span>
              </div>
            </td>
            <td>
              <div v-if="log.emails">
                <div v-for="email in log.emails" :key="email.id">
                  <a :href="`mailto:${email.address}`">{{ email.address }}</a>
                  <span>{{ ` (${email.personal})` }}</span>
                </div>
              </div>
            </td>
            <td>
              <span v-if="!log.user">{{ log.userid }}</span>
              <EntityZ :entity="log.user" format="system" v-if="log.user" />
            </td>
            <td>{{ log.timestamp | formatDateTime }}</td>
          </tr>
        </table>
      </div>
      <div
        style="overflow: auto; overflow-y: hidden"
        v-if="quote.scheduledQuoteEmails"
      >
        <br />
        <v-divider />
        <h3>Scheduled emails</h3>
        <div>
          These emails are scheduled and waiting to be sent. They will be
          cancelled if the customer unsubscribes or the status of the quote
          changes.
        </div>
        <v-btn
          @click="resendScheduledEmails()"
          color="info"
          small
          v-if="$keycloak.hasRealmRole('root')"
        >
          <v-icon>send</v-icon>Resend Scheduled Emails
        </v-btn>
        <table border="1">
          <tr>
            <th>Subject</th>
            <th>Emails</th>
            <th>Scheduled</th>
          </tr>
          <tr
            v-for="scheduleQuoteEmail in quote.scheduledQuoteEmails"
            :key="scheduleQuoteEmail.id"
          >
            <td>
              <router-link
                :to="{
                  name: 'emailTemplateRevisionSales',
                  params: {
                    id: scheduleQuoteEmail.emailTemplateRevision.emailTemplate
                      .id,
                    revisionid: scheduleQuoteEmail.emailTemplateRevision.id
                  }
                }"
                :is="
                  $keycloak.hasRealmRole('salesadmin') ? 'router-link' : 'span'
                "
                >{{
                  scheduleQuoteEmail.emailTemplateRevision.subject
                }}</router-link
              >
            </td>
            <td>
              <div v-if="scheduleQuoteEmail.scheduledQuoteEmailQuoteEmails">
                <div
                  v-for="scheduledQuoteEmailQuoteEmail in scheduleQuoteEmail.scheduledQuoteEmailQuoteEmails"
                  :key="scheduledQuoteEmailQuoteEmail.quoteEmail.id"
                >
                  <a
                    :href="`mailto:${scheduledQuoteEmailQuoteEmail.quoteEmail.email.address}`"
                    >{{
                      scheduledQuoteEmailQuoteEmail.quoteEmail.email.address
                    }}</a
                  >
                  <span>{{
                    ` (${scheduledQuoteEmailQuoteEmail.quoteEmail.email.personal})`
                  }}</span>
                </div>
              </div>
            </td>
            <!-- <td>
              <span v-if="!log.user">{{ log.userid }}</span>
              <EntityZ :entity="log.user" format="system" v-if="log.user" />
            </td> -->
            <td>{{ scheduleQuoteEmail.scheduled | formatDateTime }}</td>
          </tr>
        </table>
      </div>
      <v-snackbar
        v-model="snackbar"
        :multi-line="true"
        :timeout="3000"
        :top="true"
      >
        {{ snackbarText }}
        <v-btn color="pink" flat @click="snackbar = false">Close</v-btn>
      </v-snackbar>
      <v-dialog v-model="computedDialog" persistent>
        <v-card>
          <v-toolbar dark color="primary">
            <v-btn @click="dialogContent = null">Close</v-btn>
            <v-toolbar-title>Formatted {{ formattedStyle }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <span v-html="dialogContent" v-if="formattedStyle == 'html'" />
            <span v-else>
              <pre>{{ dialogContent }}</pre>
            </span>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import RESEND_SCHEDULED_EMAILS from "@/graphql/sales/quote/ResendScheduledEmails";
import EntityZ from "@/components/link/EntityZ";
import nbmlaw from "@/api/nbmlaw";
import nbmlawNode from "@/api/nbmlawNode";
export default {
  name: "Email",
  components: {
    EntityZ
  },
  props: ["quote", "lastUpdated", "selectedHeaderElement"],
  computed: {
    computedDialog() {
      return this.dialogContent ? true : false;
    }
  },
  data() {
    return {
      excludeOffers: false,
      callback: false,
      snackbar: false,
      snackbarText: null,
      dialogContent: null,
      formattedStyle: null
    };
  },
  methods: {
    async removeEmail(quoteEmail) {
      if (confirm("Are you sure?")) {
        await nbmlaw.delete(
          `/cors/admin/sales/quotes/${this.$route.params.id}/quoteEmails/${quoteEmail.emailid}`
        );
        // console.log(response.data);
        this.$emit("changeSelectedHeaderElement", 0);
      }
    },
    async showQuoteFormatted(value) {
      this.formattedStyle = value;
      const response = await nbmlawNode.get(
        `/api/admin/sales/quotes/${this.$route.params.id}/${value}`
      );
      // console.log(response.data);
      this.dialogContent = response.data;
    },
    async updateExcludeOffers() {
      await nbmlaw.put(
        `/cors/admin/sales/quotes/${this.$route.params.id}/excludeOffers`,
        { value: this.excludeOffers }
      );
      // console.log(response.data);
      this.$emit("changeSelectedHeaderElement", 0);
    },
    async updateCallback() {
      await nbmlaw.put(
        `/cors/admin/sales/quotes/${this.$route.params.id}/callback`,
        { value: this.callback }
      );
      // console.log(response.data);
      this.$emit("changeSelectedHeaderElement", 0);
    },
    async resendScheduledEmails() {
      await this.$apollo.mutate({
        mutation: RESEND_SCHEDULED_EMAILS,
        variables: { id: Number(this.$route.params.id) }
      });
      alert("done");
    }
  },
  watch: {
    quote: {
      immediate: true,
      handler() {
        this.excludeOffers = this.quote.excludeoffers;
        this.callback = this.quote.callback;
      }
    }
  }
};
</script>

<style scoped>
.emailArchived {
  font-size: 0.7em;
  text-decoration: line-through;
}
</style>
